import { Area } from 'react-easy-crop';

/** Загрузка изображения из data-URL/URL в HTMLImageElement. */
function loadImage(src: string, signal?: AbortSignal): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const cleanup = (): void => {
            img.onload = img.onerror = null;
            clearTimeout(timer);
            signal?.removeEventListener('abort', cancel);
        };
        const cancel = (): void => {
            cleanup();
            img.src = '';
            reject(new Error('Загрузка изображения отменена'));
        };
        const timer = setTimeout(cancel, 15000);
        img.onload = () => { cleanup(); resolve(img); };
        img.onerror = cancel;
        if (signal?.aborted) { cancel(); return; }
        signal?.addEventListener('abort', cancel, { once: true });
        img.crossOrigin = 'anonymous';
        img.src = src;
    });
}

/**
 * Средний цвет изображения (даунсэмпл в 1×1 поверх белого, чтобы учесть прозрачность).
 * Используется как фон под фото при зум-ауте — пустые углы заливаются цветом картинки.
 */
function averageColor(image: HTMLImageElement): string {
    try {
        const c = document.createElement('canvas');
        c.width = 1;
        c.height = 1;
        const ctx = c.getContext('2d');
        if (!ctx) return '#ffffff';
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, 1, 1);
        ctx.drawImage(image, 0, 0, 1, 1);
        const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
        return `rgb(${r}, ${g}, ${b})`;
    } catch {
        return '#ffffff';
    }
}

/**
 * Обрезает изображение по области (пиксели из react-easy-crop) в квадрат size×size
 * и возвращает Blob (PNG). При зум-ауте/выходе за границы пустые углы заливаются
 * средним цветом фото. На сервере изображение дополнительно нормализуется.
 */
export async function getCroppedBlob(
    imageSrc: string, crop: Area, size = 1024, mime = 'image/png', signal?: AbortSignal,
): Promise<Blob> {
    if (![size, crop.x, crop.y, crop.width, crop.height].every(Number.isFinite) ||
        size <= 0 || crop.width <= 0 || crop.height <= 0) throw new Error('Некорректная область обрезки');
    const image = await loadImage(imageSrc, signal);
    const canvas = document.createElement('canvas');
    try {
        if (image.naturalWidth * image.naturalHeight > 24_000_000) throw new Error('Изображение превышает 24 мегапикселя');
        const target = Math.max(1, Math.floor(Math.min(size, 1024, crop.width, crop.height,
            image.naturalWidth, image.naturalHeight)));
        canvas.width = canvas.height = target;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas 2D context недоступен');
        ctx.fillStyle = averageColor(image);
        ctx.fillRect(0, 0, target, target);
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, target, target);
        return await new Promise<Blob>((resolve, reject) => {
            const abort = (): void => reject(new Error('Обрезка отменена'));
            if (signal?.aborted) { abort(); return; }
            signal?.addEventListener('abort', abort, { once: true });
            try {
                canvas.toBlob((blob) => {
                    signal?.removeEventListener('abort', abort);
                    if (blob) resolve(blob);
                    else reject(new Error('Не удалось создать изображение'));
                }, mime, 0.92);
            } catch (error) {
                signal?.removeEventListener('abort', abort);
                reject(error instanceof Error ? error : new Error(String(error)));
            }
        });
    } finally {
        image.src = '';
        canvas.width = canvas.height = 0;
    }
}
